// Tiny presentational helpers shared across the role dashboards.
// Adapted from credentik-app/v2 + MemberMD V2 primitives.tsx.

import type { ReactNode, ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

// ── Card ────────────────────────────────────────────────────────────────

export function Card({
  children, className, padding = 'p-5',
}: { children: ReactNode; className?: string; padding?: string }) {
  // Voye card language: 18px radius, hairline border, soft shadow (not a flat
  // outline). Matches the mobile app's surfaces so both read as one product.
  return (
    <div
      className={cn('bg-white rounded-[var(--radius-card)]', padding, className)}
      style={{ border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-thumb)' }}
    >
      {children}
    </div>
  );
}

// ── Button (Voye spec: 16px radius, interactive blue, weights) ────────
// One CTA shape across the app so web matches the mobile app. Variants:
// primary (filled interactive blue), secondary (tinted), ghost (bordered).

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type BtnSize = 'md' | 'sm';

const BTN_VARIANTS: Record<BtnVariant, string> = {
  primary:   'text-white hover:brightness-[1.06] disabled:opacity-50',
  secondary: 'text-[var(--color-primary)] hover:brightness-[0.97] disabled:opacity-50',
  ghost:     'text-[var(--color-body)] hover:bg-[var(--color-tint-3)] disabled:opacity-50',
  danger:    'text-white hover:brightness-[1.06] disabled:opacity-50',
};

const BTN_BG: Record<BtnVariant, string> = {
  primary:   'var(--color-interactive)',
  secondary: 'var(--color-tint)',
  ghost:     'transparent',
  danger:    'var(--color-danger-fg)',
};

export function Button({
  children, variant = 'primary', size = 'md', className, fullWidth, type = 'button', ...rest
}: {
  children: ReactNode;
  variant?: BtnVariant;
  size?: BtnSize;
  className?: string;
  fullWidth?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 font-bold transition rounded-[var(--radius-button)]',
        size === 'md' ? 'px-4 py-2.5 text-[13.5px]' : 'px-3 py-1.5 text-[12px]',
        fullWidth && 'w-full',
        BTN_VARIANTS[variant],
        className,
      )}
      style={{
        background: BTN_BG[variant],
        border: variant === 'ghost' ? '1px solid var(--color-border)' : undefined,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Section header (smaller than PortalShell's TopBar h1) ────────────

export function SectionHeader({
  title, subtitle, actions,
}: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-3">
      <div>
        <h2 className="text-[15px] font-semibold tracking-tight text-[var(--color-ink)]">{title}</h2>
        {subtitle && <p className="text-[12px] text-[var(--color-secondary-2)] mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ── Stat tile (one of 4 in a typical dashboard hero row) ──────────────

interface StatTileProps {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
  trend?: { value: string; positive: boolean };
  tone?: 'default' | 'brand' | 'emerald' | 'amber' | 'rose';
}

const TILE_TONES: Record<NonNullable<StatTileProps['tone']>, string> = {
  default: 'bg-white text-[var(--color-secondary)]',
  brand:   'bg-brand-50 text-brand-700',
  emerald: 'bg-[var(--color-success-bg)] text-[var(--color-success-fg)]',
  amber:   'bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]',
  rose:    'bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)]',
};

export function StatTile({ icon: Icon, label, value, hint, trend, tone = 'default' }: StatTileProps) {
  return (
    <Card padding="p-4">
      <div className="flex items-start justify-between">
        <span className={cn('inline-flex w-9 h-9 items-center justify-center rounded-lg', TILE_TONES[tone])}>
          <Icon className="w-4 h-4" />
        </span>
        {trend && (
          <span className={cn('text-[11px] font-semibold inline-flex items-center gap-0.5',
            trend.positive ? 'text-[var(--color-success-fg)]' : 'text-[var(--color-danger-fg)]')}>
            {trend.positive ? '▲' : '▼'} {trend.value}
          </span>
        )}
      </div>
      <p className="mt-3 text-[24px] font-extrabold tracking-tight text-[var(--color-ink)] tabular-nums">{value}</p>
      <p className="text-[12px] font-semibold text-[var(--color-secondary)]">{label}</p>
      {hint && <p className="mt-1 text-[11px] text-[var(--color-muted)]">{hint}</p>}
    </Card>
  );
}

// ── Activity row (recent-events feed) ─────────────────────────────────

interface ActivityRowProps {
  icon: LucideIcon;
  iconTone?: 'brand' | 'emerald' | 'amber' | 'rose' | 'slate';
  title: ReactNode;
  meta?: ReactNode;
  time?: string;
}

const ACT_ICON_TONES: Record<NonNullable<ActivityRowProps['iconTone']>, string> = {
  brand:   'bg-brand-50 text-brand-600',
  emerald: 'bg-[var(--color-success-bg)] text-[var(--color-success-fg)]',
  amber:   'bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]',
  rose:    'bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)]',
  slate:   'bg-[var(--color-panel)] text-[var(--color-secondary)]',
};

export function ActivityRow({ icon: Icon, iconTone = 'slate', title, meta, time }: ActivityRowProps) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className={cn('inline-flex w-8 h-8 items-center justify-center rounded-full shrink-0', ACT_ICON_TONES[iconTone])}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-[var(--color-ink)] leading-snug">{title}</p>
        {meta && <p className="text-[11.5px] text-[var(--color-secondary-2)] mt-0.5">{meta}</p>}
      </div>
      {time && <span className="text-[11px] text-[var(--color-muted)] shrink-0 mt-1">{time}</span>}
    </div>
  );
}

// ── Empty state (when a section's data list is empty) ─────────────────

export function EmptyState({
  icon: Icon, title, body, action,
}: { icon: LucideIcon; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="text-center py-10 px-4">
      <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-[var(--color-panel)] text-[var(--color-muted)]">
        <Icon className="w-5 h-5" />
      </span>
      <p className="mt-3 text-[14px] font-semibold text-[var(--color-ink)]">{title}</p>
      {body && <p className="mt-1 text-[12.5px] text-[var(--color-secondary-2)] max-w-sm mx-auto">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── Demo data callout (label placeholder UI honestly) ─────────────────

export function DemoDataBadge({ note }: { note?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)] border border-[var(--color-warning-border)] text-[10px] font-semibold uppercase tracking-wider">
      Demo data{note && <span className="font-normal normal-case text-[var(--color-warning-fg)]"> · {note}</span>}
    </span>
  );
}

// ── Status pill (reusable across role pages) ───────────────────────────

/**
 * Pill tones — Web Portal v2's PILL map and Merchant Join's STATUS_TONE.
 *
 * These were Tailwind defaults (emerald-50, sky-50, violet-50, amber-50,
 * rose-50), which are NOT the design's colours and are not what the mobile app
 * uses. Pills appear on nearly every screen in this portal, so the difference
 * was most of why the two products did not look like one.
 *
 * A hex-based palette audit could not see this: Tailwind class names carry no
 * hex, so the drift was invisible to the check that caught everything else.
 */
const STATUS_TONES: Record<string, string> = {
  active:       'bg-[#E6F6EE] text-[#0A6B45]',
  approved:     'bg-[#E6F6EE] text-[#0A6B45]',
  succeeded:    'bg-[#E6F6EE] text-[#0A6B45]',
  confirmed:    'bg-[#E6F6EE] text-[#0A6B45]',
  paid:         'bg-[#E6F6EE] text-[#0A6B45]',
  delivered:    'bg-[#E6F6EE] text-[#0A6B45]',
  pending:      'bg-[#FFFAF0] text-[#8A5A00]',
  applied:      'bg-[#FFFAF0] text-[#8A5A00]',
  kyb_review:   'bg-[#FFFAF0] text-[#8A5A00]',
  submitted:    'bg-[#EAF0FE] text-[#063B9E]',
  quote_pending:'bg-[#EAF0FE] text-[#063B9E]',
  quoted:       'bg-[#EAF0FE] text-[#063B9E]',
  in_progress:  'bg-[#F1EAFE] text-[#5B21B6]',
  preparing:    'bg-[#F1EAFE] text-[#5B21B6]',
  ready:        'bg-[#F1EAFE] text-[#5B21B6]',
  failed:       'bg-[#FDECEC] text-[#B3261E]',
  disputed:     'bg-[#FDECEC] text-[#B3261E]',
  // A rejected or suspended merchant is blocked from trading. Without these
  // keys both fell through to `draft` grey, which reads as "nothing to see
  // here" — the opposite of the truth.
  rejected:     'bg-[#FDECEC] text-[#B3261E]',
  suspended:    'bg-[#FDECEC] text-[#B3261E]',
  // Money held pending a dispute is not a neutral state — it is the merchant
  // waiting on us, and it fell through to grey like a draft.
  held:         'bg-[#FDECEC] text-[#B3261E]',
  refunded:     'bg-[#E6F6EE] text-[#0A6B45]',
  // Fulfillment stages the map was missing. `ready_for_delivery` rendering as
  // a grey draft mid-delivery actively misread.
  assigned_to_merchant: 'bg-[#EAF0FE] text-[#063B9E]',
  not_started:  'bg-[#EAF0FE] text-[#063B9E]',
  ready_for_delivery:   'bg-[#F1EAFE] text-[#5B21B6]',
  out_for_delivery:     'bg-[#F1EAFE] text-[#5B21B6]',
  payment_pending:      'bg-[#FFFAF0] text-[#8A5A00]',
  customer_approval_pending: 'bg-[#FFFAF0] text-[#8A5A00]',
  // Stock, matching the three-way control's own colours.
  in_stock:     'bg-[#E6F6EE] text-[#0A6B45]',
  low:          'bg-[#FFFAF0] text-[#8A5A00]',
  out:          'bg-[#FDECEC] text-[#B3261E]',
  cancelled:    'bg-[#F1F4FA] text-[#5B6478]',
  draft:        'bg-[#F1F4FA] text-[#5B6478]',
};

export function StatusPill({ status }: { status: string }) {
  const norm = String(status).toLowerCase().replace(/\s+/g, '_');
  const tone = STATUS_TONES[norm] ?? STATUS_TONES.draft;
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium capitalize', tone)}>
      {norm.replace(/_/g, ' ')}
    </span>
  );
}
